import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, TextField, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, Alert, Grid, Divider,
  Card, CardMedia, List, ListItem, ListItemIcon, ListItemText, Tabs, Tab,
  MenuItem
} from '@mui/material';
import {
  Visibility, CheckCircle, Cancel, Delete, Image as ImageIcon, VideoLibrary,
  InsertDriveFile, PlayCircle, Download
} from '@mui/icons-material';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const AdminProducts = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [priceTypeFilter, setPriceTypeFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('none');
  const [detailDialog, setDetailDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [detailTab, setDetailTab] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/products/admin/all');
      setProducts(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleApproval = async (id, status) => {
    try {
      await api.put(`/products/${id}`, {
        status,
        approvalNotes: approvalNote,
        approvedAt: status === 'approved' ? new Date() : undefined
      });
      setApprovalNote('');
      loadProducts();
    } catch (err) { console.error(err); }
  };

  const openDetail = (product) => {
    setSelectedProduct(product);
    setDetailTab(0);
    setApprovalNote('');
    setDetailDialog(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteDialog(false);
  };

  const handleDelete = async () => {
    if (!selectedProduct?._id) return;

    setDeleting(true);
    try {
      await api.delete(`/products/${selectedProduct._id}`);
      setDeleteDialog(false);
      setDetailDialog(false);
      setSelectedProduct(null);
      loadProducts();
    } catch (err) {
      console.error(err);
    }
    setDeleting(false);
  };

  const statusColor = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'error';
    if (status === 'draft') return 'default';
    if (status === 'archived') return 'default';
    return 'warning';
  };

  const statusOptions = ['all', ...new Set(products.map((product) => product.status).filter(Boolean))];
  const categoryOptions = ['all', ...new Set(products.map((product) => product.category?.name).filter(Boolean))];
  const supplierOptions = ['all', ...new Set(products.map((product) => product.supplier?.companyName).filter(Boolean))];
  const priceTypeOptions = ['all', ...new Set(products.map((product) => product.pricing?.priceType).filter(Boolean))];

  const filtered = products.filter((product) => {
    const searchValue = search.trim().toLowerCase();
    const matchesSearch = !searchValue ||
      product.name?.toLowerCase().includes(searchValue) ||
      product.osesgCode?.toLowerCase().includes(searchValue) ||
      product.supplierProductCode?.toLowerCase().includes(searchValue) ||
      product.supplier?.companyName?.toLowerCase().includes(searchValue);

    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || product.category?.name === categoryFilter;
    const matchesSupplier = supplierFilter === 'all' || product.supplier?.companyName === supplierFilter;
    const matchesPriceType = priceTypeFilter === 'all' || product.pricing?.priceType === priceTypeFilter;

    return matchesSearch && matchesStatus && matchesCategory && matchesSupplier && matchesPriceType;
  });

  const getGroupValue = (product) => {
    if (groupBy === 'status') return product.status || 'Unspecified Status';
    if (groupBy === 'category') return product.category?.name || 'Uncategorized';
    if (groupBy === 'supplier') return product.supplier?.companyName || 'Unknown Supplier';
    if (groupBy === 'priceType') return product.pricing?.priceType || 'Unspecified Price Type';
    return 'All Products';
  };

  const groupedProducts = groupBy === 'none'
    ? [{ label: null, items: filtered }]
    : Object.entries(
        filtered.reduce((groups, product) => {
          const key = getGroupValue(product);
          if (!groups[key]) groups[key] = [];
          groups[key].push(product);
          return groups;
        }, {})
      ).map(([label, items]) => ({ label, items }));

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setSupplierFilter('all');
    setPriceTypeFilter('all');
    setGroupBy('none');
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  const p = selectedProduct;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('manage_products')}</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth placeholder="Search by name, OSESG code, supplier code, or supplier name..."
            value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth select label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} size="small">
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'all' ? 'All Statuses' : option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth select label="Category" value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)} size="small">
            {categoryOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'all' ? 'All Categories' : option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth select label="Supplier" value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)} size="small">
            {supplierOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'all' ? 'All Suppliers' : option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField fullWidth select label="Price Type" value={priceTypeFilter}
            onChange={(e) => setPriceTypeFilter(e.target.value)} size="small">
            {priceTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'all' ? 'All Price Types' : option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth select label="Group By" value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)} size="small">
            <MenuItem value="none">No Grouping</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="category">Category</MenuItem>
            <MenuItem value="supplier">Supplier</MenuItem>
            <MenuItem value="priceType">Price Type</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button fullWidth variant="outlined" onClick={resetFilters} sx={{ height: '100%' }}>
            Reset Filters
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ height: '100%' }}>
            <Chip label={`${filtered.length} products`} color="primary" variant="outlined" />
            {groupBy !== 'none' && <Chip label={`${groupedProducts.length} groups`} variant="outlined" />}
          </Box>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>OSESG Code</TableCell>
              <TableCell>Supplier Cat. #</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No products match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {groupedProducts.map((group) => (
              <React.Fragment key={group.label || 'all-products'}>
                {group.label && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ bgcolor: '#f8f9ff', borderTop: '1px solid #e0e0e0' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2" fontWeight={700}>{group.label}</Typography>
                        <Chip label={`${group.items.length} items`} size="small" variant="outlined" />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {group.items.map((product) => (
                  <TableRow key={product._id} hover>
                    <TableCell><Chip label={product.osesgCode} size="small" color="primary" /></TableCell>
                    <TableCell>
                      {product.supplierProductCode ? (
                        <Chip label={product.supplierProductCode} size="small" variant="outlined" color="secondary" />
                      ) : '-'}
                    </TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{product.supplier?.companyName || 'N/A'}</Typography>
                      <Typography variant="caption" color="text.secondary">{product.supplier?.internalCode}</Typography>
                    </TableCell>
                    <TableCell>{product.category?.name || 'N/A'}</TableCell>
                    <TableCell>
                      {product.pricing?.priceType === 'negotiable' ? 'Negotiable' :
                        `$${product.pricing?.basePrice?.toLocaleString()}`}
                    </TableCell>
                    <TableCell>
                      <Chip label={product.status} size="small" color={statusColor(product.status)} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openDetail(product)}>
                        <Visibility />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(product)}>
                        <Delete />
                      </IconButton>
                      {product.status === 'pending_approval' && (
                        <>
                          <IconButton size="small" color="success" onClick={() => handleApproval(product._id, 'approved')}>
                            <CheckCircle />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleApproval(product._id, 'rejected')}>
                            <Cancel />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Full Product Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            Product Details: {p?.osesgCode}
            <Chip label={p?.status} size="small" color={statusColor(p?.status)} sx={{ ml: 1 }} />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {p && (
            <Box>
              {/* Header Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">OSESG Code</Typography>
                    <Typography variant="body2" fontWeight={600}>{p.osesgCode}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Supplier Catalog #</Typography>
                    <Typography variant="body2" fontWeight={600}>{p.supplierProductCode || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Supplier</Typography>
                    <Typography variant="body2" fontWeight={600}>{p.supplier?.companyName}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.supplier?.internalCode}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Typography variant="body2" fontWeight={600}>{p.category?.name || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Tabs */}
              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} variant="scrollable" sx={{ mb: 1 }}>
                <Tab label="Overview" />
                <Tab label={`Images (${p.images?.length || 0})`} icon={<ImageIcon fontSize="small" />} iconPosition="start" />
                <Tab label={`Videos (${p.videos?.length || 0})`} icon={<VideoLibrary fontSize="small" />} iconPosition="start" />
                <Tab label={`Documents (${p.documents?.length || 0})`} icon={<InsertDriveFile fontSize="small" />} iconPosition="start" />
                <Tab label="Specs & Details" />
              </Tabs>

              {/* Tab 0: Overview */}
              <TabPanel value={detailTab} index={0}>
                <Typography variant="h5" gutterBottom>{p.name}</Typography>
                {p.nameAr && <Typography variant="h6" gutterBottom dir="rtl" color="text.secondary">{p.nameAr}</Typography>}

                {p.shortDescription && (
                  <Alert severity="info" sx={{ mb: 2 }}>{p.shortDescription}</Alert>
                )}

                <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
                  {p.description || 'No description provided.'}
                </Typography>
                {p.descriptionAr && (
                  <Typography variant="body1" paragraph dir="rtl" sx={{ whiteSpace: 'pre-line', bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    {p.descriptionAr}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Price</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      ${p.pricing?.basePrice || 0}
                      <Typography component="span" variant="body2" color="text.secondary"> ({p.pricing?.priceType})</Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">MOQ</Typography>
                    <Typography variant="h6" fontWeight={600}>{p.pricing?.moq || 1} units</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Lead Time</Typography>
                    <Typography variant="h6" fontWeight={600}>{p.shipping?.leadTime || 'N/A'} days</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">Custom Manufacturing</Typography>
                    <Typography variant="h6" fontWeight={600}>{p.isCustomManufacturing ? 'Yes' : 'No'}</Typography>
                  </Grid>
                </Grid>

                {p.pricing?.priceBreaks?.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>Price Breaks</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Qty Range</TableCell>
                          <TableCell>Price/Unit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {p.pricing.priceBreaks.map((pb, i) => (
                          <TableRow key={i}>
                            <TableCell>{pb.minQty} - {pb.maxQty}</TableCell>
                            <TableCell>${pb.price}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 1: Images */}
              <TabPanel value={detailTab} index={1}>
                {p.images?.length > 0 ? (
                  <Grid container spacing={2}>
                    {p.images.map((img, i) => (
                      <Grid item xs={6} sm={4} md={3} key={i}>
                        <Card>
                          <CardMedia
                            component="div"
                            sx={{
                              height: 180,
                              bgcolor: '#f5f5f5',
                              backgroundImage: `url(${img.url})`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'center',
                              backgroundSize: 'contain'
                            }}
                            aria-label={img.alt || `Product image ${i + 1}`}
                            role="img"
                          />
                          <Box sx={{ p: 1, textAlign: 'center' }}>
                            <Typography variant="caption">{img.alt || `Image ${i + 1}`}</Typography>
                            {img.isPrimary && <Chip label="Primary" size="small" color="primary" sx={{ ml: 1 }} />}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={4}>
                    <ImageIcon sx={{ fontSize: 60, color: '#ccc' }} />
                    <Typography color="text.secondary">No images uploaded by supplier</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 2: Videos */}
              <TabPanel value={detailTab} index={2}>
                {p.videos?.length > 0 ? (
                  <Grid container spacing={2}>
                    {p.videos.map((vid, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Card sx={{ p: 2 }}>
                          <Box sx={{ bgcolor: '#000', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                            <video width="100%" height="220" controls>
                              <source src={vid.url} />
                            </video>
                          </Box>
                          <Typography variant="subtitle2">{vid.title || `Video ${i + 1}`}</Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box textAlign="center" py={4}>
                    <PlayCircle sx={{ fontSize: 60, color: '#ccc' }} />
                    <Typography color="text.secondary">No operational videos uploaded</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 3: Documents */}
              <TabPanel value={detailTab} index={3}>
                {p.documents?.length > 0 ? (
                  <List>
                    {p.documents.map((doc, i) => (
                      <ListItem key={i} divider>
                        <ListItemIcon><InsertDriveFile /></ListItemIcon>
                        <ListItemText
                          primary={doc.name}
                          secondary={<Chip label={doc.type} size="small" variant="outlined" />}
                        />
                        <Button size="small" href={doc.url} target="_blank" startIcon={<Download />}>
                          View
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box textAlign="center" py={4}>
                    <InsertDriveFile sx={{ fontSize: 60, color: '#ccc' }} />
                    <Typography color="text.secondary">No documents uploaded</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 4: Specs & Details */}
              <TabPanel value={detailTab} index={4}>
                {/* Specifications */}
                {p.specifications?.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>Technical Specifications</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableBody>
                          {p.specifications.map((spec, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontWeight: 600, width: '35%', bgcolor: '#f5f5f5' }}>{spec.key}</TableCell>
                              <TableCell>{spec.value} {spec.unit}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Applications */}
                {p.applications?.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>Applications / مجالات الاستخدام</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {p.applications.map((app, i) => <Chip key={i} label={app} variant="outlined" />)}
                    </Box>
                  </Box>
                )}

                {/* Advantages */}
                {p.advantages?.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" gutterBottom>Advantages / المزايا</Typography>
                    <Grid container spacing={1}>
                      {p.advantages.map((adv, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                          <Typography variant="body2">✅ {adv}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Keywords/Tags */}
                {p.keywords?.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>Keywords</Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {p.keywords.map((kw, i) => <Chip key={i} label={kw} size="small" />)}
                    </Box>
                  </Box>
                )}

                {(!p.specifications?.length && !p.applications?.length && !p.advantages?.length) && (
                  <Box textAlign="center" py={4}>
                    <Typography color="text.secondary">No specifications or details provided yet</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Approval Section */}
              {p.status === 'pending_approval' && (
                <Box mt={3}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" color="warning.main" gutterBottom>Pending Approval</Typography>
                  <TextField fullWidth label="Approval Notes" value={approvalNote}
                    onChange={(e) => setApprovalNote(e.target.value)} multiline rows={2} sx={{ mb: 2 }} />
                  <Box display="flex" gap={2}>
                    <Button variant="contained" color="success" size="large" startIcon={<CheckCircle />}
                      onClick={() => { handleApproval(p._id, 'approved'); setDetailDialog(false); }}>
                      Approve
                    </Button>
                    <Button variant="contained" color="error" size="large" startIcon={<Cancel />}
                      onClick={() => { handleApproval(p._id, 'rejected'); setDetailDialog(false); }}>
                      Reject
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {p && (
            <Button color="error" startIcon={<Delete />} onClick={() => openDeleteDialog(p)}>
              Delete Product
            </Button>
          )}
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedProduct?.name || 'this product'}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The product will be archived and removed from supplier and public listings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminProducts;
